import { useRef, useState, type ComponentProps } from 'react';
import { CalendarDays } from 'lucide-react';
import { formatDate, parseDateInput } from '../../lib/format';
import { Input } from './Input';

export interface DateInputProps
  extends Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'defaultValue' | 'type' | 'min' | 'max' | 'prefix' | 'width'> {
  /** ISO date `yyyy-mm-dd`, or null when empty / not a real date. */
  value: string | null | undefined;
  onChange: (iso: string | null) => void;
  /** ISO bounds passed to the calendar picker (validation belongs in the zod schema). */
  min?: string;
  max?: string;
  /** Hide the calendar button (typing is always possible). */
  noPicker?: boolean;
}

/**
 * Date entry that displays `dd Mon yyyy` (e.g. "12 Oct 2026") and reports an ISO value ("2026-10-12").
 * Accepts typed dates like "12/10/2026", "12 October 2026" or "2026-10-12" and tidies them on blur.
 * A calendar button opens the browser's native date picker. Use with react-hook-form's `Controller`.
 * Put "For example, 12 Oct 2026" in the Field hint.
 */
export function DateInput({ value, onChange, onBlur, min, max, noPicker, disabled, ...rest }: DateInputProps) {
  const [text, setText] = useState(() => (value ? formatDate(value) : ''));
  const [prevValue, setPrevValue] = useState(value);
  const pickerRef = useRef<HTMLInputElement>(null);

  if (value !== prevValue) {
    setPrevValue(value);
    if ((value ?? null) !== parseDateInput(text)) setText(value ? formatDate(value) : '');
  }

  const commit = (next: string) => {
    setText(next);
    const iso = parseDateInput(next);
    setPrevValue(iso);
    onChange(iso);
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') el.showPicker();
      else el.focus();
    } catch {
      el.focus();
    }
  };

  return (
    <div className="relative max-w-[18ch] min-w-[min(14ch,100%)]">
      <Input
        type="text"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        value={text}
        disabled={disabled}
        onChange={(e) => commit(e.target.value)}
        onBlur={(e) => {
          const iso = parseDateInput(text);
          if (iso) setText(formatDate(iso));
          onBlur?.(e);
        }}
        suffix={
          noPicker ? undefined : (
            <button
              type="button"
              onClick={openPicker}
              disabled={disabled}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-primary hover:bg-primary-soft"
              aria-label="Choose date from calendar"
            >
              <CalendarDays aria-hidden="true" className="h-5 w-5" />
            </button>
          )
        }
        {...rest}
      />
      {!noPicker && (
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-0 h-px w-px opacity-0"
          value={value ?? ''}
          min={min}
          max={max}
          onChange={(e) => {
            const iso = e.target.value || null;
            setText(iso ? formatDate(iso) : '');
            setPrevValue(iso);
            onChange(iso);
          }}
        />
      )}
    </div>
  );
}
